import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const showEllipsis = totalPages > 5;

  const getVisiblePages = () => {
    if (!showEllipsis) return pages;

    // On mobile, show fewer pages
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      if (currentPage <= 2) {
        return [1, 2, '...', totalPages];
      }
      if (currentPage >= totalPages - 1) {
        return [1, '...', totalPages - 1, totalPages];
      }
      return [1, '...', currentPage, '...', totalPages];
    }

    // Desktop view
    if (currentPage <= 3) {
      return [...pages.slice(0, 4), '...', totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, '...', ...pages.slice(totalPages - 4)];
    }
    return [
      1,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages,
    ];
  };

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg text-content-secondary hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {getVisiblePages().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-sm sm:text-base ${
            page === currentPage
              ? 'bg-brand text-white'
              : 'text-content-secondary hover:text-brand'
          } ${page === '...' ? 'cursor-default' : ''}`}
          aria-label={typeof page === 'number' ? `Page ${page}` : 'More pages'}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg text-content-secondary hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Next page"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Pagination;